"use client";

import { memo } from "react";
import { useTrackList } from "@/hooks/use-track-list";
import type { Track } from "@/lib/track/track";
import { SingleLaneTrackRow, MultiLaneTrackRow } from "./track-rows";

/**
 * Vrai quand cette piste laisse un arbitrage à l'utilisateur.
 *
 * Le cas nominal n'en laisse aucun, d'où trois exclusions :
 *
 * - la piste live du clavier (et le métronome) : son audio ne se touche pas
 *   d'ici, et sa trace se masque désormais depuis les Réglages ;
 * - un morceau réduit à exactement MG + MD : c'est le sélecteur d'écoute de
 *   la toolbar qui le pilote ;
 * - un morceau d'une seule voie (le jingle de démarrage en est un) : il n'y a
 *   rien à arbitrer entre deux voies quand il n'y en a qu'une.
 *
 * Reste ce que rien d'autre ne couvre : un import multi-voix qui ne se réduit
 * pas aux deux mains, ou dont l'analyse n'a pas assigné les mains.
 */
function isManageable(track: Track): boolean {
  const controler = track.getControler();
  const lanes = track.getLanes();

  if (lanes.length < 2) {
    return false;
  }

  const hands = new Set<string>();

  for (const lane of lanes) {
    const origin = controler.getLaneOrigin?.(lane);

    if (origin?.kind !== "song-part") {
      return false;
    }

    if (origin.hand !== null) {
      hands.add(origin.hand);
    }
  }

  return !(lanes.length === 2 && hands.size === 2);
}

export const TrackList = memo(function TrackList() {
  const tracks = useTrackList();
  const manageable = tracks.filter(isManageable);

  if (manageable.length === 0) {
    return null;
  }

  return (
    <div className="shrink-0 border-t border-border-subtle flex flex-col select-none">
      {/* Header */}
      <div className="px-3 pt-3 pb-1.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-accent-bright">
          Pistes
        </span>
        <span className="text-[9px] tabular-nums text-text-muted">
          {manageable.length}
        </span>
      </div>

      {/* Track list */}
      <div className="px-2 pb-2 space-y-0.5">
        {manageable.map((track, index) => {
          const lanes = track.getLanes();

          // `isManageable` écarte déjà les pistes mono-voie ; la branche reste
          // pour que l'organisme sache rendre les deux formes si la règle
          // d'affichage venait à s'élargir.
          if (lanes.length <= 1) {
            return <SingleLaneTrackRow key={index} track={track} />;
          }

          return <MultiLaneTrackRow key={index} track={track} />;
        })}
      </div>
    </div>
  );
});
